import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState, } from 'react';
import { boardExtent, CELL, columnLabel, crossVariants, dotVariants, framePaths, gridPaths, LABEL, ringVariants, rowLabel, shipPaths, variantFor, } from './sketch.js';
/**
 * Rysuje zestaw ścieżek z rough.js. Kolor bierze z `currentColor`, czyli ze zmiennych CSS.
 *
 * `pathLength={1}` normalizuje długość ścieżki niezależnie od jej faktycznej geometrii,
 * więc `.sw-board path` w `tokens.css` może animować `stroke-dashoffset` z 1 do 0 tą samą
 * regułą dla każdej kreski — "rysowanie" bez liczenia długości w JS (`docs/05` §5.4).
 * Animacja odpala się tylko przy wstawieniu węzła do DOM-u (montaż elementu przez Reacta),
 * więc nowy statek albo nowy strzał rysuje się raz, a re-render istniejącego — nie.
 */
function Paths({ paths, fill }) {
    return (_jsx(_Fragment, { children: paths.map((p, i) => (_jsx("path", { d: p.d, pathLength: 1, stroke: "currentColor", strokeWidth: p.strokeWidth, fill: p.fill ? (fill ?? 'currentColor') : 'none', strokeLinecap: "round" }, i))) }));
}
/**
 * Plansza w stylu szkicu — SVG, nie canvas.
 *
 * SVG daje za darmo zdarzenia na poziomie pojedynczego pola, fokus klawiatury,
 * atrybuty ARIA i skalowanie bez rozmycia. Przy 100 polach wydajność DOM-u nie jest problemem;
 * problemem byłaby regeneracja kresek — patrz `sketch.ts`.
 */
export function SketchBoard({ size, seed, fleet, shots = [], ghost, aim, recent = [], interactive = false, disabled = false, dimmed = false, ariaLabel, onCellActivate, onCellHover, onCellPointerDown, onCellPointerMove, onCellPointerUp, }) {
    const extent = boardExtent(size);
    const [focus, setFocus] = useState({ x: 0, y: 0 });
    const svgRef = useRef(null);
    /**
     * Punkt ekranu → pole planszy. Liczone z `getBoundingClientRect`, a nie z `elementFromPoint`,
     * bo przy przeciąganiu z przechwyconym wskaźnikiem zdarzenia nie docierają do pól.
     */
    const cellFromPoint = useCallback((clientX, clientY) => {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0)
            return null;
        const scale = extent / rect.width;
        const x = Math.floor(((clientX - rect.left) * scale - LABEL) / CELL);
        const y = Math.floor(((clientY - rect.top) * scale - LABEL) / CELL);
        if (x < 0 || y < 0 || x >= size || y >= size)
            return null;
        return { x, y };
    }, [extent, size]);
    const frame = useMemo(() => framePaths(size, seed), [size, seed]);
    const grid = useMemo(() => gridPaths(size, seed), [size, seed]);
    const cells = useMemo(() => {
        const out = [];
        for (let y = 0; y < size; y++)
            for (let x = 0; x < size; x++)
                out.push({ x, y });
        return out;
    }, [size]);
    const hulls = useMemo(() => (fleet ?? []).map((ship) => ({ ship, paths: shipPaths(ship, seed) })), [fleet, seed]);
    function move(event, dx, dy) {
        event.preventDefault();
        const next = {
            x: Math.min(size - 1, Math.max(0, focus.x + dx)),
            y: Math.min(size - 1, Math.max(0, focus.y + dy)),
        };
        setFocus(next);
        const target = event.currentTarget.ownerSVGElement?.querySelector(`[data-cell="${next.x}-${next.y}"]`);
        target?.focus();
    }
    function onKeyDown(event, cell) {
        switch (event.key) {
            case 'ArrowLeft':
                return move(event, -1, 0);
            case 'ArrowRight':
                return move(event, 1, 0);
            case 'ArrowUp':
                return move(event, 0, -1);
            case 'ArrowDown':
                return move(event, 0, 1);
            case 'Enter':
            case ' ':
                event.preventDefault();
                if (!disabled)
                    onCellActivate?.(cell);
                return;
            default:
        }
    }
    const shotAt = new Map(shots.map((s) => [`${s.x}-${s.y}`, s]));
    const recentKeys = new Set(recent.map((c) => `${c.x}-${c.y}`));
    return (_jsxs("svg", { className: dimmed ? 'sw-board sw-board--dimmed' : 'sw-board', viewBox: `0 0 ${extent} ${extent}`, width: "100%", role: interactive ? 'grid' : 'img', "aria-label": ariaLabel, ref: svgRef, style: { touchAction: onCellPointerDown ? 'none' : 'manipulation' }, onPointerLeave: () => onCellHover?.(null), onPointerDown: (event) => {
            const cell = cellFromPoint(event.clientX, event.clientY);
            if (cell)
                onCellPointerDown?.(cell, event);
        }, onPointerMove: (event) => {
            if (!onCellPointerMove)
                return;
            onCellPointerMove(cellFromPoint(event.clientX, event.clientY));
        }, onPointerUp: (event) => {
            if (!onCellPointerUp)
                return;
            onCellPointerUp(cellFromPoint(event.clientX, event.clientY));
        }, children: [_jsx("title", { children: ariaLabel }), _jsxs("g", { fill: "currentColor", style: { color: 'var(--ink-soft)' }, fontFamily: "var(--font-hand)", fontSize: 18, children: [Array.from({ length: size }, (_, i) => (_jsx("text", { x: LABEL + i * CELL + CELL / 2, y: LABEL - 6, textAnchor: "middle", children: columnLabel(i) }, columnLabel(i)))), Array.from({ length: size }, (_, i) => (_jsx("text", { x: LABEL - 8, y: LABEL + i * CELL + CELL / 2 + 6, textAnchor: "end", children: rowLabel(i) }, rowLabel(i))))] }), _jsx("g", { style: { color: 'var(--board-grid)' }, children: _jsx(Paths, { paths: grid }) }), _jsx("g", { style: { color: 'var(--board-frame)' }, children: _jsx(Paths, { paths: frame }) }), _jsx("g", { style: { color: 'var(--ship-stroke)' }, children: hulls.map(({ ship, paths }) => (_jsx(Paths, { paths: paths, fill: "var(--ship-fill)" }, ship.id))) }), ghost && (_jsx("g", { style: { color: ghost.legal ? 'var(--ghost-ok)' : 'var(--ghost-bad)', opacity: 0.75 }, children: _jsx(Paths, { paths: shipPaths(ghost.ship, seed + 999, !ghost.legal) }) })), recent.map((cell) => (_jsx("rect", { x: LABEL + cell.x * CELL + 2, y: LABEL + cell.y * CELL + 2, width: CELL - 4, height: CELL - 4, rx: 5, fill: "var(--mark-recent)", pointerEvents: "none" }, `recent-bg-${cell.x}-${cell.y}`))), shots.map((shot) => {
                const variant = variantFor(shot.x, shot.y);
                const hit = shot.result !== 'miss';
                const pulse = recentKeys.has(`${shot.x}-${shot.y}`);
                return (_jsx("g", { transform: `translate(${LABEL + shot.x * CELL}, ${LABEL + shot.y * CELL})`, style: { color: hit ? 'var(--mark-hit)' : 'var(--mark-miss)' }, children: _jsx("g", { className: pulse ? 'sw-recent-pulse' : undefined, children: _jsx("g", { className: hit ? 'sw-hit-mark' : undefined, children: _jsx(Paths, { paths: hit ? (crossVariants[variant] ?? []) : (dotVariants[variant] ?? []) }) }) }) }, `${shot.x}-${shot.y}`));
            }), recent.length > 0 && (_jsx("g", { style: { color: 'var(--ink)' }, opacity: 0.2, pointerEvents: "none", children: recent.map((cell) => (_jsx("g", { transform: `translate(${LABEL + cell.x * CELL}, ${LABEL + cell.y * CELL})`, children: _jsx(Paths, { paths: ringVariants[variantFor(cell.x, cell.y)] ?? [] }) }, `recent-ring-${cell.x}-${cell.y}`))) })), aim && (_jsx("g", { style: { color: 'var(--aim)' }, pointerEvents: "none", children: _jsx("rect", { x: LABEL + aim.x * CELL + 2, y: LABEL + aim.y * CELL + 2, width: CELL - 4, height: CELL - 4, fill: "none", stroke: "currentColor", strokeWidth: 2.5, strokeDasharray: "6 4", rx: 4 }) })), interactive && (_jsx("g", { children: cells.map((cell) => {
                    const shot = shotAt.get(`${cell.x}-${cell.y}`);
                    const label = `${columnLabel(cell.x)}${rowLabel(cell.y)}, ${shot ? (shot.result === 'miss' ? 'pudło' : 'trafienie') : 'nieostrzelane'}`;
                    const focused = focus.x === cell.x && focus.y === cell.y;
                    return (
                    // biome-ignore lint/a11y/useSemanticElements: w SVG nie ma odpowiednika <td>; siatka planszy musi być opisana rolami ARIA
                    _jsx("rect", { "data-cell": `${cell.x}-${cell.y}`, x: LABEL + cell.x * CELL, y: LABEL + cell.y * CELL, width: CELL, height: CELL, fill: "transparent", role: "gridcell", "aria-label": label, "aria-disabled": disabled || undefined, tabIndex: focused ? 0 : -1, style: { cursor: disabled ? 'not-allowed' : 'crosshair', outlineOffset: -2 }, onKeyDown: (event) => onKeyDown(event, cell), onFocus: () => setFocus(cell), onPointerEnter: () => onCellHover?.(cell), onClick: () => !disabled && onCellActivate?.(cell) }, `${cell.x}-${cell.y}`));
                }) }))] }));
}
//# sourceMappingURL=SketchBoard.js.map